// ── Persistent TTS player (iOS pre-prime pattern) ────────────────────────────
// iOS Safari only allows audio.play() within a short window after a user gesture.
// Fix: keep ONE Audio element alive and re-prime it on every touchend/click so it
// is always "user-activated". When TTS fires later (even after multiple async hops)
// we reuse that same activated element — iOS permits play() on it indefinitely.
//
// touchend (not touchstart) is the correct iOS activation event.
// We guard with _ttsActive so the global prime never interrupts live playback.

const createSilentWavDataUrl = (): string => {
  const sampleRate = 8000;
  const durationSeconds = 0.25;
  const sampleCount = Math.floor(sampleRate * durationSeconds);
  const bytesPerSample = 2;
  const dataSize = sampleCount * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeAscii = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeAscii(36, 'data');
  view.setUint32(40, dataSize, true);

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return `data:audio/wav;base64,${window.btoa(binary)}`;
};

const SILENT_WAV = createSilentWavDataUrl();
const ttsPlayer  = new Audio();
ttsPlayer.preload = 'auto';
let   _ttsActive = false;
let   _ttsAutoplayHold = false;

const _prime = () => {
  if (_ttsActive) return;

  ttsPlayer.src = SILENT_WAV;

  void ttsPlayer.play().then(() => {
    // If another playback claimed the persistent player while Safari was
    // resolving the priming play(), do not pause that real playback.
    if (!_ttsActive && ttsPlayer.src === SILENT_WAV) {
      ttsPlayer.pause();
      ttsPlayer.currentTime = 0;
    }
  }).catch(() => {});
};
document.addEventListener('touchend', _prime, { passive: true });
document.addEventListener('click',    _prime);

/** Call synchronously inside any user-gesture handler that will trigger TTS. */
export const primeTTSPlayer = (): void => {
  _prime();
};

/**
 * Keep the persistent player actively playing real silent PCM while an async
 * Speaking response is being generated. Browsers can discard a short-lived
 * autoplay grant once a priming clip has been paused; keeping the same media
 * element alive bridges the user's Send gesture to the later TTS playback.
 */
export const holdTTSPlayerForAutoplay = (): void => {
  if (_ttsActive) return;

  _ttsActive = true;
  _ttsAutoplayHold = true;

  ttsPlayer.pause();
  ttsPlayer.currentTime = 0;
  ttsPlayer.loop = true;
  ttsPlayer.src = SILENT_WAV;
  ttsPlayer.onended = null;
  ttsPlayer.onerror = null;

  void ttsPlayer.play().catch(() => {
    if (_ttsAutoplayHold) {
      _ttsAutoplayHold = false;
      _ttsActive = false;
      ttsPlayer.loop = false;
    }
  });
};

/**
 * Claim the already user-activated persistent player for a manual TTS action.
 * Keeping the same HTMLAudioElement is important on iOS Safari: a new element
 * created after an async /speech.php request may require a second tap.
 */
export const claimTTSPlayer = (): HTMLAudioElement => {
  _ttsActive = true;
  _ttsAutoplayHold = false;
  ttsPlayer.loop = false;
  return ttsPlayer;
};

/** Release a claim/hold without destroying the persistent iOS player. */
export const releaseTTSPlayer = (): void => {
  _ttsActive = false;
  _ttsAutoplayHold = false;
  ttsPlayer.loop = false;
  ttsPlayer.pause();
  ttsPlayer.onended = null;
  ttsPlayer.onerror = null;
};

export const isPersistentTTSPlayer = (
  audio: HTMLAudioElement | null
): boolean => audio === ttsPlayer;

export const textToSpeech = async (text: string): Promise<Blob | null> => {
  try {
    const response = await fetch('/speech.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ input: text, voice: 'nova' }),
    });
    if (!response.ok) throw new Error(`Error: ${response.statusText}`);
    return await response.blob();
  } catch (error) {
    console.error('Error fetching speech audio:', error);
    return null;
  }
};

// ── Chunked TTS ───────────────────────────────────────────────────────────────
// Splits the response into sentence-sized pieces, fires all TTS requests in
// parallel, then plays them back-to-back via the pre-primed ttsPlayer.

const splitIntoChunks = (text: string, maxWords = 30): string[] => {
  const parts = text.match(/[^.!?]+[.!?]+['"']?\s*|[^.!?]+$/g) ?? [text];
  const chunks: string[] = [];
  let current = '';
  for (const part of parts) {
    const combined = current + part;
    if (current && combined.trim().split(/\s+/).length > maxWords) {
      chunks.push(current.trim());
      current = part;
    } else {
      current = combined;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
};

const fetchChunkBlob = (text: string): Promise<Blob | null> =>
  fetch('/speech.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input: text, voice: 'nova' }),
  })
    .then(r => (r.ok ? r.blob() : null))
    .catch(() => null);

export const playChunkedTTS = (
  text: string,
  onDone: () => void,
): (() => void) => {
  const chunks = splitIntoChunks(text);
  if (chunks.length === 0) { onDone(); return () => {}; }

  let canceled = false;
  const blobPromises = chunks.map(fetchChunkBlob);

  const run = async () => {
    _ttsActive = true;

    // IMPORTANT: if sendInput() started the autoplay hold, keep the silent
    // audio looping while /speech.php generates the first real chunk.
    // Releasing the hold here would let the 250 ms silent WAV finish before
    // the async TTS request resolves, which makes browsers treat the later
    // play() as a brand-new autoplay attempt and block it.
    let firstPlayableChunk = true;

    for (const promise of blobPromises) {
      if (canceled) break;
      const blob = await promise;
      if (!blob || canceled) break;
      const url = URL.createObjectURL(blob);

      await new Promise<void>((resolve) => {
        if (canceled) {
          URL.revokeObjectURL(url);
          resolve();
          return;
        }

        const finishChunk = () => {
          URL.revokeObjectURL(url);
          resolve();
        };

        ttsPlayer.onended = finishChunk;
        ttsPlayer.onerror = finishChunk;

        // Transition the SAME already-playing media element from the silent
        // hold to the first real TTS chunk only after that chunk is ready.
        // Once the first audible chunk starts, the hold is no longer needed.
        if (firstPlayableChunk) {
          ttsPlayer.loop = false;
          firstPlayableChunk = false;
        }

        ttsPlayer.src = url;

        void ttsPlayer.play().then(() => {
          _ttsAutoplayHold = false;
        }).catch((error) => {
          console.warn('RETZIA Course Activity TTS autoplay was blocked.', error);
          _ttsAutoplayHold = false;
          finishChunk();
        });
      });
    }

    _ttsActive = false;
    _ttsAutoplayHold = false;
    ttsPlayer.loop = false;
    if (!canceled) onDone();
  };

  run();

  return () => {
    canceled = true;
    _ttsActive = false;
    _ttsAutoplayHold = false;
    ttsPlayer.loop = false;
    ttsPlayer.pause();
    ttsPlayer.onended = null;
    ttsPlayer.onerror = null;
  };
};
